import { useState, useEffect } from "react";
import DemoLayout from "@/components/demo/DemoLayout";
import { useTheme } from "@/contexts/ThemeContext";
import { Sun, Moon, User, Shield } from "lucide-react";

export default function DemoSettings() {
  const { isDark, toggleTheme } = useTheme();
  const [userInfo, setUserInfo] = useState({ fullName: "", email: "", role: "" });

  useEffect(() => {
    try {
      const stored = JSON.parse(localStorage.getItem("userInfo") || "{}");
      setUserInfo({
        fullName: stored.fullName || "Executive",
        email: stored.email || "",
        role: stored.role || "executive",
      });
    } catch { setUserInfo({ fullName: "Executive", email: "", role: "executive" }); }
  }, []);

  return (
    <DemoLayout>
      <div className="space-y-5 max-w-2xl" data-testid="settings-page">
        <div>
          <h1 className="text-xl font-semibold" style={{ color: "var(--text-primary)" }}>Settings</h1>
          <p className="text-xs mt-0.5" style={{ color: "var(--text-muted)" }}>Account and display preferences</p>
        </div>

        {/* Theme */}
        <div className="card-m p-5">
          <h2 className="text-sm font-semibold mb-4" style={{ color: "var(--text-primary)" }}>Appearance</h2>
          <div className="flex items-center justify-between">
            <div>
              <div className="text-[13px] font-medium" style={{ color: "var(--text-primary)" }}>Theme</div>
              <div className="text-[12px]" style={{ color: "var(--text-muted)" }}>Choose light or dark mode</div>
            </div>
            <button
              onClick={toggleTheme}
              className="flex items-center gap-2 px-4 py-2 rounded-lg transition-all"
              style={{ background: "var(--surface-muted)", color: "var(--text-primary)" }}
              data-testid="theme-toggle-setting"
            >
              {isDark ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
              <span className="text-[13px]">{isDark ? "Switch to Light" : "Switch to Dark"}</span>
            </button>
          </div>
        </div>

        {/* Account */}
        <div className="card-m p-5">
          <h2 className="text-sm font-semibold mb-4" style={{ color: "var(--text-primary)" }}>Account</h2>
          <div className="space-y-3 text-[13px]">
            {[
              ["Email", userInfo.email],
              ["Role", userInfo.role.charAt(0).toUpperCase() + userInfo.role.slice(1)],
              ["Access", "All 6 regions"],
              ["Name", userInfo.fullName],
            ].map(([label, val]) => (
              <div key={label} className="flex justify-between py-2" style={{ borderBottom: "var(--border-width) solid var(--border-subtle)" }}>
                <span style={{ color: "var(--text-muted)" }}>{label}</span>
                <span className="font-medium" style={{ color: "var(--text-primary)" }}>{val}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Permissions */}
        <div className="card-m p-5">
          <h2 className="text-sm font-semibold mb-4" style={{ color: "var(--text-primary)" }}>Permissions</h2>
          <div className="flex flex-wrap gap-2">
            {["Executive Dashboard", "All Regions", "Export", "Alerts", "Intelligence", "Heatmap"].map(p => (
              <span key={p} className="badge-m badge-success text-[10px]">{p}</span>
            ))}
            {["Talent Strategy", "Workforce Planning", "ROI Analysis"].map(p => (
              <span key={p} className="badge-m badge-muted text-[10px]">
                <Shield className="w-3 h-3" /> {p} (Locked)
              </span>
            ))}
          </div>
        </div>
      </div>
    </DemoLayout>
  );
}
